import { FetchResult, InternalRefetchQueriesInclude, useMutation } from '@apollo/client';
import { useI18n } from '@i18n';
import { useSnackbar } from 'notistack';
import { CONFIRM_FILE_UPLOAD } from '@/graphql/common.mutations';
import { SnackbarTimeouts } from '../components/NotistackProvider';

type UseUpdateFilenameProps = {
  updateFilename(
    fileId: number,
    fileName: string,
    refetchQueries?: ((result: FetchResult<unknown>) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude,
  ): Promise<string>;
};

/**
 * Publishes a file after its upload finished, setting the real filename in the same step.
 *
 * The row is created up front (with the sub-building draft) to generate the s3_key and uploadUrl,
 * and stays at `uploading: true` until the backend has verified the object in S3.
 */
export default function useUpdateFilename(): UseUpdateFilenameProps {
  const { t } = useI18n();
  const { enqueueSnackbar } = useSnackbar();

  const [confirmFileUpload] = useMutation(CONFIRM_FILE_UPLOAD, {
    onError: () => {
      enqueueSnackbar(t('UpdateFileName_Snackbar-Error'), {
        variant: 'error',
        autoHideDuration: SnackbarTimeouts.Error,
      });
    },
  });

  const updateFilename = async (
    fileId: number,
    fileName: string,
    refetchQueries?: ((result: FetchResult<unknown>) => InternalRefetchQueriesInclude) | InternalRefetchQueriesInclude,
  ): Promise<string> => {
    const res = await confirmFileUpload({
      variables: {
        fileId,
        fileName,
      },
      refetchQueries,
    });

    return res?.data?.confirmFileUpload?.name ?? '';
  };

  return {
    updateFilename,
  };
}
